/**
 * Nextdoor Adapter
 *
 * Adapts standardized event payloads into Nextdoor Universal Pixel events.
 * Returns a unified data structure used for both the browser pixel (ndp) and
 * the Conversion API (server-side).
 *
 * Each adapted event carries the Nextdoor pixel event name (e.g. ADD_TO_CART;
 * the server lowercases it into the Conversion API form add_to_cart), a
 * unique event_id shared by the browser pixel and the Conversion API so
 * Nextdoor deduplicates the two, the custom data object, the source URL and
 * any advanced-matching user data.
 *
 * The Pixel Manager sends the standardized events Nextdoor supports for
 * e-commerce: PAGE_VIEW, VIEW_CONTENT on product pages, ADD_TO_CART,
 * INITIATE_CHECKOUT, SEARCH and PURCHASE with order ID, order value and
 * product details.
 *
 * @see https://developer.nextdoor.com/reference/for-net-new-advertisers
 * @see https://developer.nextdoor.com/reference/conversion-api
 */

(function (pmw, undefined) {

	class NextdoorAdapter {

		/**
		 * Adapt an event for Nextdoor
		 *
		 * @param {Object} eventData - Standardized event data
		 * @param {string} eventName - Standardized event name
		 * @param {Object} context - Additional context
		 * @returns {Object|null} - Nextdoor formatted event or null to skip
		 */
		adapt(eventData, eventName, context) {
			const methodName = `adapt${this.toPascalCase(eventName)}`;

			if (typeof this[methodName] === "function") {
				return this[methodName](eventData, context);
			}

			// Nextdoor does not track every standardized event - skip the rest.
			return null;
		}

		toPascalCase(str) {
			if (typeof str !== "string") return "";
			return str.replace(/(^|_)(\w)/g, (_, __, c) => c.toUpperCase());
		}

		/**
		 * Get the product identifier for Nextdoor
		 *
		 * Uses the configured dynamic remarketing ID type so the IDs match
		 * the product catalog submitted to Nextdoor.
		 */
		getProductId(product) {
			if (!product) return null;

			const idType = pmwDataLayer?.pixels?.nextdoor?.dynamic_remarketing?.id_type;

			if (idType && product.dyn_r_ids && product.dyn_r_ids[idType]) {
				return String(product.dyn_r_ids[idType]);
			}

			return product.id ? String(product.id) : null;
		}

		/**
		 * Round a value to two decimals; Nextdoor expects numeric prices
		 * with up to two decimal places.
		 */
		toPrice(value) {
			const price = Math.round(Number(value) * 100) / 100;
			return Number.isFinite(price) ? price : 0;
		}

		/**
		 * Format a monetary value the way the Nextdoor Conversion API expects
		 * the custom order_value: a plain numeric string, with the currency in
		 * a separate custom field. The browser pixel expects the concatenated
		 * form ("USD412.21") instead; pmw.nextdoorTrackEvent converts it.
		 */
		toOrderValue(value) {
			return this.toPrice(value).toFixed(2);
		}

		/**
		 * Base Nextdoor event data shared by the browser pixel and the
		 * Conversion API: the event ID for deduplication, the source URL and
		 * any advanced-matching user data and click ID for the server side.
		 */
		getBaseData() {
			const data = {
				event_id  : pmw.getRandomEventId(),
				source_url: window.location.href,
				customer  : pmw.getNextdoorCustomerData(),
			};

			const ndclid = pmw.getNextdoorClickId();
			if (ndclid) data.customer.click_id = ndclid;

			return data;
		}

		/**
		 * Build a Nextdoor product context entry
		 */
		getProductContextEntry(product, quantity, price) {
			const id = this.getProductId(product);
			if (!id) return null;

			return {
				id          : id,
				content_name: product.name || "",
				quantity    : Number(quantity) || 1,
				item_price  : this.toPrice(price),
			};
		}

		/**
		 * Adapt PageView event
		 *
		 * Nextdoor's "PAGE_VIEW" event, the basis for Nextdoor's conversion
		 * event configuration. Mirrors the base code snippet, which tracks a
		 * page view on every page.
		 */
		adaptPageView(eventData, context) {
			const data       = this.getBaseData();
			data.event_name  = "PAGE_VIEW";
			data.custom_data = {};
			return data;
		}

		/**
		 * Adapt ViewItem event
		 *
		 * Nextdoor's "VIEW_CONTENT" event marks a product detail page view.
		 */
		adaptViewItem(eventData, context) {
			const entry = this.getProductContextEntry(eventData.product, 1, eventData.product?.price);
			if (!entry) return null;

			const data       = this.getBaseData();
			data.event_name  = "VIEW_CONTENT";
			data.custom_data = {
				product_context: [entry],
			};
			return data;
		}

		/**
		 * Adapt AddToCart event
		 */
		adaptAddToCart(eventData, context) {
			const product  = eventData.product;
			const quantity = Number(product?.quantity) || 1;
			const entry    = this.getProductContextEntry(product, quantity, product?.price);
			if (!entry) return null;

			const data       = this.getBaseData();
			data.event_name  = "ADD_TO_CART";
			data.custom_data = {
				product_context: [entry],
			};
			return data;
		}

		/**
		 * Adapt BeginCheckout event
		 *
		 * Nextdoor's "INITIATE_CHECKOUT" event. Cart data is only attached
		 * when the tracked cart has contents, because an empty tracked cart
		 * at checkout time usually means the cart was populated outside of
		 * client-side tracking.
		 */
		adaptBeginCheckout(eventData, context) {
			const data       = this.getBaseData();
			data.event_name  = "INITIATE_CHECKOUT";
			data.custom_data = {};

			if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {

				if (pmwDataLayer?.shop?.currency) {
					data.custom_data.order_value = this.toOrderValue(pmw.getCartValue());
					data.custom_data.currency    = pmwDataLayer.shop.currency;
				}

				const items = [];

				Object.values(pmwDataLayer.cart).forEach(product => {
					const entry = this.getProductContextEntry(product, product.quantity, product.price);
					if (entry) items.push(entry);
				});

				if (items.length) data.custom_data.product_context = items;
			}

			return data;
		}

		/**
		 * Adapt Search event
		 */
		adaptSearch(eventData, context) {
			const data       = this.getBaseData();
			data.event_name  = "SEARCH";
			data.custom_data = {};
			return data;
		}

		/**
		 * Adapt Purchase event
		 *
		 * Nextdoor's "PURCHASE" event carries the order ID, the order value
		 * and all purchased products. The event_id mirrors the server-side
		 * Conversion API purchase ID ("pmw_" + order ID) so the browser pixel
		 * and the Conversion API purchase event are deduplicated.
		 */
		adaptPurchase(eventData, context) {
			if (!pmwDataLayer?.order) return null;

			const items = [];

			Object.values(pmwDataLayer.order.items || {}).forEach(item => {

				// Resolve the purchased variation when variations are output,
				// falling back to the parent product.
				let product = pmwDataLayer.products?.[item.id];

				if (pmwDataLayer?.shop?.variations_output && item.variation_id && 0 !== item.variation_id) {
					product = pmwDataLayer.products?.[item.variation_id] || product;
				}

				const id = this.getProductId(product) || (item.id ? String(item.id) : null);
				if (!id) return;

				items.push({
					id          : id,
					content_name: item.name || product?.name || "",
					quantity    : Number(item.quantity) || 1,
					item_price  : this.toPrice(item.price),
				});
			});

			const customData = {
				order_id: String(pmwDataLayer.order.number || pmwDataLayer.order.id),
			};

			if (pmwDataLayer.order.currency) {
				customData.order_value = this.toOrderValue(pmwDataLayer.order.value?.marketing);
				customData.currency    = pmwDataLayer.order.currency;
			}

			if (items.length) customData.product_context = items;

			const data       = this.getBaseData();
			data.event_id    = pmwDataLayer.order.id ? "pmw_" + String(pmwDataLayer.order.id) : data.event_id;
			data.event_name  = "PURCHASE";
			data.custom_data = customData;
			return data;
		}
	}

	// Register the adapter with S2S support
	const adapter = new NextdoorAdapter();
	pmw.registerAdapter("nextdoor", adapter, {
		supportsS2S               : true,
		supportsEventDeduplication: true,
		supportsPageViewS2S       : true,
		isActive                  : () => pmwDataLayer?.pixels?.nextdoor?.loaded === true && pmw.consent.categories.get().marketing === true,
		isConfigured              : () => !!pmwDataLayer?.pixels?.nextdoor?.pixel_id,
	});

}(window.pmw = window.pmw || {}));
